// src/components/NotificationPanel.jsx
import React, { useEffect, useState, useCallback } from "react";
import "./NotificationPanel.css";
import api from "./api";

const NotificationPanel = ({ onClose }) => {

  function normalizeNotification(n = {}) {
    return {
      id: n.id,
      title: n.title || "Notification",
      body: n.body || "",                              
      read: !!(n.is_read ?? n.read),                  
      created_ago: n.created_ago || "",
      extra_data: n.extra_data || {},
    };
  }
  const search = (typeof window !== "undefined" && window.location)
    ? window.location.search
    : "";
  const MOCK_NOTIFS =
    localStorage.getItem("mock_notifs") === "1" ||
    (process.env.NODE_ENV !== "production" &&
      new URLSearchParams(search).has("mock"));

  function generateMockNotifications() {
    return [
      {
        id: 90001,
        title: "Welcome to CURU",
        body: "Thanks for joining! Start by completing your skin profile.",
        extra_data: {
          avatar: "/default-user-icon.png",
          url: "/onboarding"
        },
        is_read: false,
        created_ago: "5 min ago"
      },
      {
        id: 90002,
        title: "New reply in your post",
        body: "Alex replied to your question about moisturizers.",
        extra_data: {
          avatar: "/default-user-icon.png",
          url: "/community/post/736"
        },
        is_read: false,
        created_ago: "24 min ago"
      },
      {
        id: 90003,
        title: "Daily tip",
        body: "Hydration is key—remember to drink enough water today",
        extra_data: {
          avatar: "/default-user-icon.png"
        },
        is_read: true,
        created_ago: "1 hour ago"
      },
      {
        id: 90004,
        title: "Sale just for you",
        body: "Products matched to your skin concern: Dryness.",
        extra_data: {
          avatar: "/default-user-icon.png",
          url: "/shop?match=my-skin"
        },
        is_read: true,
        created_ago: "2 hours ago"
      },
      {
        id: 90005,
        title: "Security notice",
        body: "New login from Chrome on macOS.",
        extra_data: {
          avatar: "/default-user-icon.png"
        },
        is_read: true,
        created_ago: "Yesterday"
      }
    ];
  }
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/profile/notifications");
      const data = res.data;
  let list = Array.isArray(data)
    ? data
    : data?.notifications || data?.results || data?.items || [];

  if (MOCK_NOTIFS && (!list || list.length === 0)) {
    list = generateMockNotifications();
  }

  setNotifications((list || []).map(normalizeNotification));
  } catch (e) {
    console.error("Error fetching notifications:", e);
    if (MOCK_NOTIFS) {
      const list = generateMockNotifications();
      setNotifications(list);
      console.info("[Mock] Fallback injected due to fetch error");
    } else {
      setNotifications([]);
    }
  } finally {
    setLoading(false);
  }
},[MOCK_NOTIFS]);

  const clearNotifications = async () => {
    const confirmed = window.confirm("Clear all notifications?");
    if (!confirmed) return;
    const prev = notifications;
    setNotifications([]); // optimistic
    try {
      const res = await api.delete("/profile/notifications");
      if (!(res.status >= 200 && res.status < 300)) {
        setNotifications(prev); // rollback
      }
    } catch (e) {
      console.error("Error clearing notifications:", e);
      setNotifications(prev); // rollback
    }
  };

  const toggleRead = async (id, nextRead) => {
    const prev = notifications;
    // optimistic UI
    setNotifications((p) => p.map((n) => (n.id === id ? { ...n, read: nextRead } : n)));
    try {
      const res = await api.patch(`/profile/notifications/${id}`, { is_read: nextRead });
      if (!(res.status >= 200 && res.status < 300)) {
        setNotifications(prev); // rollback on unexpected status
      }
    } catch (e) {
      console.error("Failed to mark as read/unread:", e);
      setNotifications(prev); // rollback on error
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const allRead = notifications.length > 0 && unreadCount === 0;
  const allUnread = notifications.length > 0 && unreadCount === notifications.length;

  const markAll = async (nextRead) => {
    if (notifications.length === 0) return;
    const prev = notifications;
    setNotifications(prev.map((n) => ({ ...n, read: nextRead })));
    try {
      await Promise.all(
        prev.map((n) => api.patch(`/profile/notifications/${n.id}`, { is_read: nextRead }))
      );
    } catch (e) {
      console.error("Failed to update all notifications:", e);
      setNotifications(prev); // rollback
    }
  };

  const markAllRead = () => markAll(true);
  const markAllUnread = () => markAll(false);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="notification-panel" role="dialog" aria-label="Notifications">
      <div className="notification-header">
        <span>Notifications</span>
        <div className="notification-actions">
          <button
            className="mark-btn ghost"
            onClick={fetchNotifications}
            disabled={loading}
            title="Refresh notifications"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            className="clear-btn"
            onClick={clearNotifications}
            disabled={notifications.length === 0 || loading}
            title={notifications.length === 0 ? "No notifications to clear" : "Clear all"}
          >
            Clear All
          </button>
          <button className="notification-close-btn" onClick={onClose} aria-label="Close panel">
            &times;
          </button>
        </div>
      </div>

      <div className="notification-bulk">
        <div className="bulk-right">
          <button
            className="mark-btn"
            onClick={markAllRead}
            disabled={notifications.length === 0 || allRead || loading}
            title="Mark every message as read"
          >
            Mark all read
          </button>
          <button
            className="mark-btn"
            onClick={markAllUnread}
            disabled={notifications.length === 0 || allUnread || loading}
            title="Mark every message as unread"
          >
            Mark all unread
          </button>
        </div>
      </div>

      {notifications.length === 0 && !loading ? (
        <div className="notification-empty">
          <div className="title">You're all caught up</div>
          <div className="hint">New updates will appear here.</div>
        </div>
      ) : (
      notifications.map((n) => (
        <div
          key={n.id}
          className={`notification-item ${n.read ? "is-read" : "is-unread"}`}
        >
          <img
            src={n.extra_data?.avatar || n.extra_data?.icon || "/default-user-icon.png"}
            alt=""
            className="notification-icon"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="notification-content">
            <div className="notification-title">
              {!n.read && <span className="unread-dot" aria-hidden="true" />}
              {n.title || "Notification"}
            </div>

            <div className="notification-body">{n.body}</div>
            <div className="notification-time">{n.created_ago}</div>

            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              {n.extra_data?.url && (
              <a
                className="read-toggle-btn"
                href={n.extra_data.url}
                target={/^https?:\/\//.test(n.extra_data.url) ? "_blank" : undefined}
                rel={/^https?:\/\//.test(n.extra_data.url) ? "noopener noreferrer" : undefined}
              >
                Open
              </a>
            )}
              <button
                className="read-toggle-btn"
                onClick={() => toggleRead(n.id, !n.read)}
                aria-label={n.read ? "Mark as unread" : "Mark as read"}
                title={n.read ? "Mark as unread" : "Mark as read"}
              >
                {n.read ? "Mark as unread" : "Mark as read"}
              </button>
            </div>
          </div>
        </div>
      ))
      )}
    </div>
  );
};

export default NotificationPanel;
